import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"
import {
  associatePriorityWithPreference,
  dataLoaded,
  demotePreferenceRank,
  dissociatePriorityWithPreference,
  promotePreferenceRank,
  removePreference,
} from "./actions"
import {
  LearnersPreferences,
  Preference,
  PreferenceId,
  Priority,
  PriorityId,
  SubjectId,
  ValidPreference,
} from "./types"
import { snapshotSlice } from "./snapshot"

const preferencesAdapter = createEntityAdapter<Preference>({
  sortComparer: (a, b) => a.rank - b.rank,
})
const selectors = preferencesAdapter.getSelectors()

const restoreSnapshot: (state: PreferenceSlice) => PreferenceSlice = (
  state,
) => {
  return {
    ...state.snapshot,
    selectedPreferenceId: state.selectedPreferenceId,
    snapshot: preferencesAdapter.getInitialState(),
  }
}

const buildEducatorName = ({
  teacherName,
  listingProfileName,
}: Pick<
  ValidPreference["classDocument"],
  "teacherName" | "listingProfileName"
>) =>
  teacherName !== null
    ? `${teacherName} (${listingProfileName})`
    : listingProfileName

function buildPreference(
  subjectId: SubjectId,
  incoming: LearnersPreferences["preferences"][number],
): Preference {
  const { id, priorities, classDocument, ...rest } = incoming

  const preference = {
    ...rest,
    id: PreferenceId({ id }),
    subjectId,
  }

  if (classDocument !== null) {
    return {
      ...preference,
      priorities: priorities.map(({ priority }) =>
        PriorityId({ id: priority }),
      ),
      educatorName: buildEducatorName(classDocument),
      classDocument: {
        ...classDocument,
        timetablePriorities: classDocument.timetablePriorities.map(({ uuid }) =>
          PriorityId({ id: uuid }),
        ),
      },
    }
  }

  return {
    ...preference,
    priorities: [],
    educatorName: null,
    classDocument: null,
  }
}

type PreferenceSlice = ReturnType<typeof preferencesSlice.selectSlice>
export const preferencesSlice = createSlice({
  name: "preferences",
  initialState: preferencesAdapter.getInitialState({
    snapshot: preferencesAdapter.getInitialState(),
    selectedPreferenceId: null as PreferenceId | null,
  }),
  reducers: {
    preferenceSelected(state, action: PayloadAction<PreferenceId | null>) {
      state.selectedPreferenceId = action.payload
    },
  },
  selectors: {
    selectPreference: selectors.selectById,
    selectSelectedPreference: createSelector([(state) => state], (state) =>
      state.selectedPreferenceId === null
        ? null
        : selectors.selectById(state, state.selectedPreferenceId),
    ),
    selectIneligiblePreferences: createSelector(
      [
        selectors.selectAll,
        (_, { subjectId }: { subjectId: SubjectId }) => subjectId,
      ],
      (preferences, subjectId) =>
        preferences.filter(
          (p) =>
            p.subjectId === subjectId &&
            p.issues.includes("student_ineligible"),
        ),
    ),
    selectSubjectPreferences: createSelector(
      [
        selectors.selectAll,
        (_, { subjectId }: { subjectId: SubjectId }) => subjectId,
      ],
      (preferences, subjectId) =>
        preferences.filter((p) => p.subjectId === subjectId),
    ),
    selectInvalidPreferences: createSelector(
      [
        selectors.selectAll,
        (_, { subjectId }: { subjectId: SubjectId }) => subjectId,
      ],
      (preferences, subjectId) =>
        preferences.filter(
          (p) => p.subjectId === subjectId && p.classDocument === null,
        ),
    ),
  },
  extraReducers(builder) {
    builder.addCase(dataLoaded, (state, action) => {
      preferencesAdapter.removeAll(state)
      state.selectedPreferenceId = null

      action.payload.forEach(({ id, preferences }) => {
        const subjectId = SubjectId({ id })

        preferencesAdapter.addMany(
          state,
          preferences.map((preference) =>
            buildPreference(subjectId, preference),
          ),
        )
      })
    })

    builder.addAsyncThunk(removePreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)

        const preference = action.meta.arg.preference
        preferencesAdapter.removeOne(
          state,
          preferencesAdapter.selectId(preference),
        )

        preferencesAdapter.updateMany(
          state,
          selectors
            .selectAll(state)
            .filter((p) => p.subjectId === preference.subjectId)
            .map((pref, index) => ({
              id: PreferenceId({ id: pref.id }),
              changes: { rank: index + 1 },
            })),
        )
      },
    })

    builder.addAsyncThunk(associatePriorityWithPreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)

        const { preference, priority } = action.meta.arg

        const newPriorities = new Set(preference.priorities).add(
          PriorityId({ id: priority.uuid }),
        )

        preferencesAdapter.updateOne(state, {
          id: preferencesAdapter.selectId(preference),
          changes: { priorities: [...newPriorities] },
        })

        dissaociatePriority(state, priority)
      },
    })

    builder.addAsyncThunk(dissociatePriorityWithPreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        dissaociatePriority(state, action.meta.arg.priority)
      },
    })

    builder.addAsyncThunk(promotePreferenceRank, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        const { preference } = action.meta.arg
        swapRanks(state, preference, preference.rank - 1)
      },
    })

    builder.addAsyncThunk(demotePreferenceRank, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        const { preference } = action.meta.arg
        swapRanks(state, preference, preference.rank + 1)
      },
    })
  },
})

function dissaociatePriority(state: PreferenceSlice, priority: Priority) {
  if (priority.preferenceId === null) {
    return
  }

  const preference = selectors.selectById(state, priority.preferenceId)

  preferencesAdapter.updateOne(state, {
    id: priority.preferenceId,
    changes: {
      priorities: preference.priorities.filter((id) => priority.uuid !== id),
    },
  })
}

function swapRanks(
  state: PreferenceSlice,
  preference: Preference,
  desiredRank: number,
) {
  const currentRank = preference.rank

  const currentDesiredRankPreference = selectors
    .selectAll(state)
    .filter((p) => p.subjectId === preference.subjectId)
    .find((p) => p.rank === desiredRank)

  if (!currentDesiredRankPreference) return

  preferencesAdapter.updateMany(state, [
    {
      id: preferencesAdapter.selectId(preference),
      changes: {
        rank: desiredRank,
      },
    },
    {
      id: preferencesAdapter.selectId(currentDesiredRankPreference),
      changes: {
        rank: currentRank,
      },
    },
  ])
}

const { preferenceSelected } = preferencesSlice.actions
export const preferencesActions = { preferenceSelected }

const {
  selectPreference,
  selectSelectedPreference,
  selectIneligiblePreferences,
  selectSubjectPreferences,
  selectInvalidPreferences,
} = preferencesSlice.selectors
export const preferencesSelectors = {
  selectPreference,
  selectSelectedPreference,
  selectIneligiblePreferences,
  selectSubjectPreferences,
  selectInvalidPreferences,
}
